import { buildCuedBlocks, blocksToPlainText, buildMetadataHeaderBlock } from './buildCued.js';
import { buildProseBlocks } from './buildProse.js';
import { buildCsv } from './buildCsv.js';
import { buildDocx } from './buildDocx.js';
import { getDefaultLabels } from './formatters.js';
import { normalizeTranscriptExportConfig } from './options.js';

/**
 * @typedef {Object} TranscriptExportContext
 * @property {{ id?: string, name?: string, username?: string, frameRate: number|string, dropFrame: boolean }} project
 * @property {Array<{ type?: string, value?: string }>} [metadata]
 * @property {import('./formatters.js').TranscriptLabelSet} [labels]
 */

/**
 * Build transcript export content for the given event group and options.
 * Events are expected to already be prepared (cloned, trial text, TC offset).
 *
 * @param {object} eventGroup
 * @param {Partial<import('./options.js').TranscriptExportOptions>} rawOptions
 * @param {TranscriptExportContext} ctx
 * @returns {Promise<{ blob: Blob, filename: string, extension: string }>}
 */
export async function buildTranscriptExport(eventGroup, rawOptions, ctx) {
    const options = normalizeTranscriptExportConfig(rawOptions);
    const labels = { ...getDefaultLabels(), ...(ctx.labels || {}) };
    const project = ctx.project;
    const fileName = eventGroup.name || 'transcript';

    if (options.fileFormat === 'csv') {
        const csvContent = buildCsv(eventGroup, options, project, labels);
        return {
            blob: new Blob([csvContent], { type: 'text/csv;charset=utf-8' }),
            filename: `${fileName}.csv`,
            extension: 'csv',
        };
    }

    /** @type {import('./buildCued.js').TranscriptBlock[]} */
    const blocks = [];

    if (options.exportMetadata) {
        blocks.push(buildMetadataHeaderBlock(project, ctx.metadata || [], labels));
    }

    if (options.layout === 'prose') {
        blocks.push(...buildProseBlocks(eventGroup, options, project, labels));
    } else {
        blocks.push(...buildCuedBlocks(eventGroup, options, project, labels));
    }

    if (options.fileFormat === 'txt') {
        const text = blocksToPlainText(blocks);
        return {
            blob: new Blob([text], { type: 'text/plain;charset=utf-8' }),
            filename: `${fileName}.txt`,
            extension: 'txt',
        };
    }

    const blob = await buildDocx(blocks, fileName);
    return {
        blob,
        filename: `${fileName}.docx`,
        extension: 'docx',
    };
}

export {
    getDefaultTranscriptExportOptions,
    normalizeTranscriptExportConfig,
    toPresetConfig,
    loadSavedTranscriptExportOptions,
    saveTranscriptExportOptions,
    layoutFromLegacyPreset,
    isCuedMetadataEnabled,
    getDefaultFieldLayout,
    normalizeFieldLayout,
    isFieldEnabled,
    PROSE_FIELD_IDS,
    LABELED_FIELD_IDS,
    TRANSCRIPT_WRAP_OPTIONS,
    TRANSCRIPT_FIELD_IDS,
    flagsFromFieldLayout,
} from './options.js';

export { getDefaultLabels } from './formatters.js';
